import {
  setCart,
  addOptimisticProduct,
  updateCartItemQuantity,
  removeOptimisticProduct,
} from "../redux/cartReducer";
import { Dispatch } from "@reduxjs/toolkit";
import { generateCartCustomId } from "../utils/cartHelpers";
import { addCartItem } from "./cartGroupItemApis";
import store from "../redux/store";
import CartOperationManager from "../services/CartOperationManager";
import { processCartResponse } from "../utils/cartResponseUtils";
import { registerItemUpdate } from "../redux/cartOperationsAdapter";
import { metaPixelCatalogService } from "../services/metaPixelCatalogService";

// Define interfaces for product and cart types
interface CartProduct {
  cartCustomId: string;
  productId: string;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: string;
    name: string;
    sku?: string;
    stock: number;
    [key: string]: any;
  };
  [key: string]: any;
}

// Define type for location state
interface LocationState {
  location: {
    deliveryFeeZone?: {
      documentId: string;
      name?: string;
    } | null;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Get destination zone ID from Redux store
 */
function getDestinationZoneId(): string | undefined {
  const state = store.getState() as LocationState;
  return state.location?.deliveryFeeZone?.documentId;
}

/**
 * Transform product data for Meta Pixel tracking
 */
function transformProductForMetaPixel(
  productData: any,
  productId: string,
  cartCustomId: string
): any {
  if (!productData) return null;

  return {
    id: productId,
    sku: productData.sku || cartCustomId.split("-")[0] || productId,
    name: productData.name || "Unknown Product",
    category: productData.category?.name || productData.category || "Unknown",
    brand: productData.partner?.name || productData.brand || "Unknown",
    price: productData.price?.regPrice || 0,
    discountPrice: productData.price?.discount?.newPrice || undefined,
    stock: productData.stock || 0,
    images: productData.images || [],
    url: productData.url || productId,
    description: productData.description || "",
    partner: productData.partner,
  };
}

/**
 * Add a product to the cart with optimistic UI updates
 * Enhanced to handle various cart scenarios with a more centralized approach
 */
export const addToCart = async (
  productId: string,
  quantity: number,
  options: {
    cartCustomId?: string;
    customizableOptions?: any[];
    groupingAttributes?: any[];
    notes?: string;
    productData?: any;
    destinationZoneId?: string;
    originZoneId?: string;
  } = {},
  dispatch: Dispatch<any>,
  toggleMiniCart?: () => void,
  getState?: () => any
) => {
  // Generate a unique operation ID for tracking this specific add operation
  const operationId = `addToCart-${productId}-${Date.now()}-${Math.random()
    .toString(36)
    .substring(2, 10)}`;

  // Initialize cartCustomId with a default value
  let cartCustomId = options.cartCustomId || "";

  try {
    // Get destination zone from options or from Redux
    const destinationZoneId =
      options.destinationZoneId || getDestinationZoneId();

    // Get product's origin zone (for group determination)
    const originZoneId =
      options.originZoneId ||
      options.productData?.partner?.originZones?.deliveryFeeZone?.documentId;

    // Generate cart custom ID if not provided
    cartCustomId =
      options.cartCustomId ||
      generateCartCustomId(productId, {
        groupingAttributes: options.groupingAttributes,
        customizableOptions: options.customizableOptions,
      });

    // Calculate prices
    const basePrice = options.productData?.price?.regPrice || 0;
    const discountPrice = options.productData?.price?.discount?.newPrice;
    const effectivePrice =
      discountPrice !== undefined ? discountPrice : basePrice;
    const optionsSurcharge = (options.customizableOptions || []).reduce(
      (sum, option) => sum + (option.priceSurcharge || 0),
      0
    );
    const pricePerItem = effectivePrice + optionsSurcharge;
    const finalPrice = pricePerItem * quantity;

    // Check if cartId exists in localStorage
    const cartId = localStorage.getItem("cartId");
    const isNewCart = !cartId;

    console.log(
      `📋 [addToCart] Starting with ${
        isNewCart ? "new cart" : "existing cart"
      } for product ${productId}`
    );

    // Build optimistic product object
    const optimisticProduct = {
      cartCustomId,
      productId,
      quantity,
      price: pricePerItem,
      finalPrice: finalPrice,
      product: {
        id: productId,
        name: options.productData?.name || "Adding to cart...",
        sku: options.productData?.sku || cartCustomId.split("-")[0],
        stock: quantity,
        images: options.productData?.images || [],
        url: options.productData?.url || productId,
        price: {
          regPrice: basePrice,
          discount: options.productData?.price?.discount,
        },
        partner: options.productData?.partner
          ? {
              ...options.productData.partner,
              originZones: {
                deliveryFeeZone: {
                  documentId: originZoneId,
                },
              },
            }
          : undefined,
      },
      customizableOptions: options.customizableOptions || [],
      groupingAttributes: options.groupingAttributes || [],
      isOptimistic: true,
      operationId,
    };

    // Find if product exists in cart and in which group
    let existingItem = null;
    let existingGroupId = null;
    let correctGroupForOriginZone = null;

    // Only check existing cart if getState is provided and we have a cartId
    if (!isNewCart && getState) {
      const currentCart = getState().cart;

      if (currentCart?.groups) {
        // First check: Find exact matching item
        for (const group of currentCart.groups) {
          if (!group?.items) continue;

          const item = group.items.find(
            (p: CartProduct) => p.cartCustomId === cartCustomId
          );
          if (item) {
            existingItem = item;
            existingGroupId = group.id;
            break;
          }
        }

        // Second check: If no matching item but we have originZoneId,
        // find appropriate group for this origin zone
        if (!existingItem && originZoneId) {
          for (const group of currentCart.groups) {
            const firstProduct = group.items?.[0];
            if (
              firstProduct?.product?.partner?.originZones?.deliveryFeeZone
                ?.documentId === originZoneId
            ) {
              correctGroupForOriginZone = group.id;
              break;
            }
          }
        }
      }
    }

    // If toggleMiniCart is provided, show the mini cart immediately for better UX
    if (toggleMiniCart) {
      toggleMiniCart();
    }

    // SCENARIO 1: Existing item in cart - update quantity
    if (existingItem && existingGroupId) {
      console.log(
        `🔄 [addToCart] Updating existing item: ${cartCustomId} in group ${existingGroupId}`
      );

      const newQuantity = existingItem.quantity + quantity;
      const newFinalPrice = pricePerItem * newQuantity;

      // Register the operation with CartOperationManager through the adapter
      const operation = registerItemUpdate(
        dispatch,
        existingGroupId,
        cartCustomId,
        newQuantity
      );

      // Optimistic update
      dispatch(
        updateCartItemQuantity({
          cartCustomId,
          quantity: newQuantity,
          price: pricePerItem,
          finalPrice: newFinalPrice,
        })
      );

      try {
        const response = await addCartItem(
          productId,
          quantity,
          cartCustomId,
          options.groupingAttributes || [],
          options.customizableOptions || [],
          options.notes || "",
          destinationZoneId
        );

        if (response) {
          dispatch(setCart(processCartResponse(response)));

          if (isNewCart && response.cartId) {
            localStorage.setItem("cartId", response.cartId);
          }

          // Track Meta Pixel add to cart event for quantity update
          const metaPixelProduct = transformProductForMetaPixel(
            options.productData,
            productId,
            cartCustomId
          );
          if (metaPixelProduct) {
            metaPixelCatalogService.trackAddToCart(
              metaPixelProduct,
              quantity,
              options.customizableOptions,
              options.groupingAttributes
            );
          }
        }

        operation.complete(true);
      } catch (apiError) {
        console.error("❌ [addToCart] API call failed:", apiError);
        operation.complete(false);
        throw apiError;
      }
    }
    // SCENARIO 2: New item but we have a matching group for origin zone
    else if (correctGroupForOriginZone) {
      console.log(
        `➕ [addToCart] Adding to existing group ${correctGroupForOriginZone} with matching origin zone`
      );

      // Add optimistic product
      dispatch(
        addOptimisticProduct({
          product: {
            ...optimisticProduct,
            groupId: correctGroupForOriginZone,
          },
          operationId,
        })
      );

      try {
        const response = await addCartItem(
          productId,
          quantity,
          cartCustomId,
          options.groupingAttributes || [],
          options.customizableOptions || [],
          options.notes || "",
          destinationZoneId
        );

        if (response) {
          dispatch(setCart(processCartResponse(response)));

          if (isNewCart && response.cartId) {
            localStorage.setItem("cartId", response.cartId);
          }

          // Track Meta Pixel add to cart event for new item in existing group
          const metaPixelProduct = transformProductForMetaPixel(
            options.productData,
            productId,
            cartCustomId
          );
          if (metaPixelProduct) {
            metaPixelCatalogService.trackAddToCart(
              metaPixelProduct,
              quantity,
              options.customizableOptions,
              options.groupingAttributes
            );
          }
        }

        CartOperationManager.completeOperation(operationId);
      } catch (apiError) {
        console.error("❌ [addToCart] API call failed:", apiError);

        dispatch(
          removeOptimisticProduct({
            operationId,
            cartCustomId,
          })
        );

        CartOperationManager.failOperation(operationId, apiError);
        throw apiError;
      }
    }
    // SCENARIO 3: New item/group or new cart
    else {
      console.log(
        `🆕 [addToCart] Adding brand new item${isNewCart ? " to new cart" : ""}`
      );

      // Add optimistic product
      dispatch(
        addOptimisticProduct({
          product: optimisticProduct,
          operationId,
        })
      );

      try {
        const response = await addCartItem(
          productId,
          quantity,
          cartCustomId,
          options.groupingAttributes || [],
          options.customizableOptions || [],
          options.notes || "",
          destinationZoneId
        );

        if (response) {
          dispatch(setCart(processCartResponse(response)));

          if (isNewCart && response.cartId) {
            localStorage.setItem("cartId", response.cartId);
          }

          // Find the new group that contains this item
          if (response.groups) {
            for (const group of response.groups) {
              if (
                group.items?.some(
                  (p: CartProduct) => p.cartCustomId === cartCustomId
                )
              ) {
                CartOperationManager.completeOperation(operationId);
                break;
              }
            }
          }

          // Track Meta Pixel add to cart event for new item/group
          const metaPixelProduct = transformProductForMetaPixel(
            options.productData,
            productId,
            cartCustomId
          );
          if (metaPixelProduct) {
            metaPixelCatalogService.trackAddToCart(
              metaPixelProduct,
              quantity,
              options.customizableOptions,
              options.groupingAttributes
            );
          }
        }
      } catch (apiError) {
        console.error("❌ [addToCart] API call failed:", apiError);

        dispatch(
          removeOptimisticProduct({
            operationId,
            cartCustomId,
          })
        );

        CartOperationManager.failOperation(operationId, apiError);
        throw apiError;
      }
    }

    return { success: true, cartCustomId };
  } catch (error) {
    console.error("❌ [addToCart] Failed to add product to cart:", error);

    dispatch(
      removeOptimisticProduct({
        operationId,
        cartCustomId,
      })
    );

    CartOperationManager.failOperation(operationId, error);
    throw error;
  }
};
